// Robust headless-status parser (fn-69.2, R2).
//
// Pipeline: extract → validate → repair → fallback.
//
//   1. Cap the raw block size (a pathological megabyte of text is never
//      a status line — bail to fallback early).
//   2. Strip code fences (```json … ``` / ``` … ```).
//   3. Slice the first balanced `{ … }` object (string/escape-aware so a
//      brace inside a string value doesn't end the slice early). This
//      tolerates a JSON object followed by trailing prose.
//   4. `JSON.parse` → shape-validate (`isHeadlessStatus`).
//   5. On a structural parse failure, run a MINIMAL json-repair (strip
//      trailing commas, normalize smart quotes) and reparse + validate.
//   6. On ANY miss, return a typed `fallback` result — NEVER throw, and
//      NEVER surface the raw JSON/garbage to the caller.
//
// A non-JSON turn is normal prose in chat mode; the caller decides what
// `fallback` means per mode (chat → render the text as prose; headless →
// show the neutral status). This module stays mode-agnostic.

import {
  type HeadlessStatus,
  isHeadlessStatus,
  normalizeHeadlessStatus,
} from './status-contract.js';

/** Largest raw block we will attempt to parse, in characters. */
export const MAX_STATUS_BLOCK_CHARS = 16 * 1024;

export type ParseStatusResult =
  | { readonly outcome: 'status'; readonly status: HeadlessStatus }
  | { readonly outcome: 'fallback'; readonly reason: ParseFallbackReason };

export type ParseFallbackReason =
  | 'empty'
  | 'oversized'
  | 'no-json-object'
  | 'unparseable'
  | 'shape-invalid';

function fallback(reason: ParseFallbackReason): ParseStatusResult {
  return { outcome: 'fallback', reason };
}

function ok(status: HeadlessStatus): ParseStatusResult {
  return { outcome: 'status', status: normalizeHeadlessStatus(status) };
}

/**
 * Strip a single leading/trailing markdown code fence if the text is
 * fence-wrapped. Handles ```json … ``` and bare ``` … ```. Leaves text
 * untouched when there is no enclosing fence.
 */
function stripCodeFence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed.startsWith('```')) {
    return trimmed;
  }
  // Drop the opening fence line (``` optionally followed by a language
  // tag) and the closing fence.
  const withoutOpen = trimmed.replace(/^```[^\n]*\n?/, '');
  const withoutClose = withoutOpen.replace(/\n?```$/, '');
  return withoutClose.trim();
}

/**
 * Return the substring spanning the first balanced `{ … }` object, or
 * `null` when there is no opening brace or the braces never balance.
 * String-aware: braces inside double-quoted JSON strings are ignored,
 * honoring backslash escapes.
 */
function sliceFirstJsonObject(text: string): string | null {
  const start = text.indexOf('{');
  if (start === -1) {
    return null;
  }
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
    } else if (ch === '{') {
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0) {
        return text.slice(start, i + 1);
      }
    }
  }
  return null;
}

/**
 * Minimal, conservative JSON repair for the two failure modes the model
 * actually produces: trailing commas before `}`/`]`, and smart quotes
 * substituted for straight quotes. We do NOT attempt aggressive repair
 * (quoting unquoted keys, etc.) — a heavily-malformed block should fall
 * back to a neutral status, not be coerced into a wrong one.
 */
function repairJson(candidate: string): string {
  return candidate
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/,\s*([}\]])/g, '$1');
}

function tryParseAndValidate(candidate: string): HeadlessStatus | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(candidate);
  } catch {
    return null;
  }
  return isHeadlessStatus(parsed) ? parsed : null;
}

/**
 * Parse one agent text block into a `HeadlessStatus` or a typed
 * fallback. Never throws.
 */
export function parseStatusBlock(raw: string): ParseStatusResult {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return fallback('empty');
  }
  if (raw.length > MAX_STATUS_BLOCK_CHARS) {
    return fallback('oversized');
  }

  const unfenced = stripCodeFence(raw);
  const candidate = sliceFirstJsonObject(unfenced);
  if (candidate === null) {
    return fallback('no-json-object');
  }

  // 1st pass: parse the sliced object as-is.
  const direct = tryParseAndValidate(candidate);
  if (direct !== null) {
    return ok(direct);
  }

  // The slice parsed but failed the shape check → it IS valid JSON, just
  // not a status. Distinguish that from a true parse failure so the
  // reason is accurate (and so we don't waste a repair pass on a
  // well-formed-but-wrong object).
  let parsedButWrongShape = false;
  try {
    JSON.parse(candidate);
    parsedButWrongShape = true;
  } catch {
    parsedButWrongShape = false;
  }

  // 2nd pass: minimal repair, then reparse + validate.
  const repaired = tryParseAndValidate(repairJson(candidate));
  if (repaired !== null) {
    return ok(repaired);
  }

  return fallback(parsedButWrongShape ? 'shape-invalid' : 'unparseable');
}
